import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Pencil, Plus } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { SectionTitle } from '@/components/ui/SectionTitle';
import { AddReaderDialog } from '@/components/readers/AddReaderDialog';
import { useReadersStore } from '@/store/readers.store';
import { cn } from '@/lib/cn';

/**
 * Readers — list + manage every reader on the account. The grid is driven
 * entirely off the Zustand cache (the bootstrap already populated it), and
 * mutations flow back through the same store so the header reader selector
 * stays in sync.
 */
export function ReadersPage() {
  const readers = useReadersStore((s) => s.readers);
  const selectedId = useReadersStore((s) => s.selectedReaderId);
  const setSelectedReaderId = useReadersStore((s) => s.setSelectedReaderId);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // `?new=1` deep-link opens the Add Reader dialog — used by the header
  // dropdown's "Add a reader" quick action.
  const [dialogOpen, setDialogOpen] = useState(() => searchParams.get('new') === '1');

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setDialogOpen(true);
      // Clean the URL once we've consumed the flag so a refresh doesn't
      // keep re-opening the dialog.
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <AppShell mode="kid">
      <div className="flex flex-col gap-6">
        <SectionTitle
          kicker="Account"
          heading="Readers on this account"
          action={
            <Button onClick={() => setDialogOpen(true)} size="md">
              <Plus className="h-4 w-4" aria-hidden />
              Add reader
            </Button>
          }
        />

        {readers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white p-8 text-center">
            <p className="font-display text-[18px] font-bold text-or-slate-900">No readers yet</p>
            <p className="mt-1 text-[13px] text-or-slate-600">
              Add your first reader to start getting tailored recommendations.
            </p>
            <Button className="mt-4" onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              Add a reader
            </Button>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {readers.map((reader) => {
              const isActive = reader.id === selectedId;
              return (
                <li
                  key={reader.id}
                  className={cn(
                    'group relative flex flex-col gap-4 rounded-2xl border bg-white p-5 transition-shadow hover:shadow-or-pop',
                    isActive
                      ? 'border-or-orange-300 ring-2 ring-or-orange-300/40'
                      : 'border-or-slate-200',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <ReaderAvatar name={reader.name} avatarSlug={reader.avatarSlug} size="lg" />
                    <div className="min-w-0">
                      <div className="truncate font-display text-[18px] font-bold text-or-slate-900">
                        {reader.name}
                      </div>
                      {isActive ? (
                        <span className="mt-0.5 inline-flex rounded-full bg-or-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-or-orange-800">
                          Active
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setSelectedReaderId(reader.id)}
                          className="mt-0.5 text-[12px] font-semibold text-or-orange-600 hover:text-or-orange-800"
                        >
                          Read as {reader.name}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {reader.readingLevels.map((level) => (
                      <Chip key={level.labelId} variant="blue">
                        {level.labelValue}
                      </Chip>
                    ))}
                    {reader.interests.slice(0, 5).map((interest) => (
                      <Chip key={interest.labelId} variant="cream">
                        {interest.labelValue}
                      </Chip>
                    ))}
                    {reader.interests.length > 5 ? (
                      <Chip variant="outline">+{reader.interests.length - 5}</Chip>
                    ) : null}
                    {reader.interests.length === 0 && reader.readingLevels.length === 0 ? (
                      <span className="text-[12px] text-or-slate-500">No interests set yet.</span>
                    ) : null}
                  </div>

                  <div className="mt-auto flex items-center justify-end">
                    <Link
                      to={`/readers/${reader.id}`}
                      className="inline-flex items-center gap-1 rounded-full border border-or-slate-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-or-slate-700 transition-colors hover:border-or-orange-300 hover:text-or-orange-700"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                      Edit
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AddReaderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={(reader) => navigate(`/readers/${reader.id}`)}
      />
    </AppShell>
  );
}
